/**
 * taskDue — how a task's status and due date read, on every surface (#904).
 *
 * Statuses: the open/done/closed sets are the backend's
 * (`models/activity.py` TASK_DONE_STATUSES / TASK_CLOSED_STATUSES); a literal
 * in both test files pins them to each other. OPEN is "not closed", so an
 * agent-created `pending` task and a missing status both count as open.
 *
 * Due dates: `due_date` reaches the browser as a date ('2026-10-20'), an ISO
 * datetime ('2026-10-20T09:00:00') or a serialised datetime. The due DAY is
 * always its first 10 characters, on both sides of the wire — it is a calendar
 * day, not an instant, so it is never converted through a zone (lib/timezone's
 * `formatInZone` would shift it a day west of UTC). Only "today" depends on a
 * zone: the viewer's profile zone, falling back to the browser's (D4). An
 * unknown zone never throws; it falls back like an empty one.
 */
import { addDaysKey, dayKeyParts, MONTH_NAMES, todayKey } from './calendar/zonedCalendar';
import { browserTimeZone, isValidTimeZone, parseInstant } from './timezone';

export const TASK_DONE_STATUSES = new Set(['completed', 'sent', 'done']);
export const TASK_CLOSED_STATUSES = new Set([...TASK_DONE_STATUSES, 'cancelled', 'canceled', 'skipped']);

const statusOf = (taskOrStatus) => String(
    (typeof taskOrStatus === 'string' ? taskOrStatus : taskOrStatus?.status) ?? '',
).trim().toLowerCase();

/** A task row, or a bare status string, that is finished one way or another. */
export const isTaskClosed = (taskOrStatus) => TASK_CLOSED_STATUSES.has(statusOf(taskOrStatus));

/** Not closed: planned, pending, in progress, or no status at all. */
export const isTaskOpen = (taskOrStatus) => !isTaskClosed(taskOrStatus);

/** Finished successfully (completed, sent or done); a cancelled or skipped task is closed but not done. */
export const isTaskDone = (taskOrStatus) => TASK_DONE_STATUSES.has(statusOf(taskOrStatus));

/** The word on a status pill: Open, Done, Cancelled or Skipped. */
export function taskStatusLabel(taskOrStatus) {
    const status = statusOf(taskOrStatus);
    if (TASK_DONE_STATUSES.has(status)) return 'Done';
    if (status === 'cancelled' || status === 'canceled') return 'Cancelled';
    if (status === 'skipped') return 'Skipped';
    return 'Open';
}

/**
 * The due day ('YYYY-MM-DD') of a `due_date` value, or null when there is none
 * or it is not a real calendar date.
 */
export function dueDayKey(value) {
    const raw = value instanceof Date && !Number.isNaN(value.getTime())
        ? value.toISOString()
        : value;
    if (typeof raw !== 'string') return null;
    const key = raw.slice(0, 10);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
    if (!match) return null;
    const probe = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return probe.toISOString().slice(0, 10) === key ? key : null;
}

/**
 * Today's day key in `zone`. An empty or unknown zone falls back to the
 * browser's, then UTC, so a bad stored profile zone cannot break a render.
 */
export function viewerTodayKey(zone, now = new Date()) {
    const resolved = isValidTimeZone(zone) ? zone.trim() : browserTimeZone();
    try {
        return todayKey(resolved, now);
    } catch {
        return todayKey('UTC', now);
    }
}

/**
 * 'overdue' | 'today' | 'upcoming' for a due value, or 'none' when it has no
 * usable date. Pure in (due, zone, now).
 */
export function dueState(due, zone, now = new Date()) {
    const key = dueDayKey(due);
    if (!key) return 'none';
    const today = viewerTodayKey(zone, now);
    if (key < today) return 'overdue';
    return key === today ? 'today' : 'upcoming';
}

/** 'Today', 'Tomorrow', 'Oct 24' (the year is added outside this one); '' without a date. */
export function formatDue(due, zone, now = new Date()) {
    const key = dueDayKey(due);
    if (!key) return '';
    const today = viewerTodayKey(zone, now);
    if (key === today) return 'Today';
    if (key === addDaysKey(today, 1)) return 'Tomorrow';
    const { year, month, day } = dayKeyParts(key);
    const label = `${MONTH_NAMES[month].slice(0, 3)} ${day}`;
    return String(year) === today.slice(0, 4) ? label : `${label}, ${year}`;
}

/**
 * A calendar day as 'Oct 24, 2026' (the first 10 characters of `due`, never zone-shifted); '' without a
 * date. For a surface with no viewer zone to say "Today" against, such as the Outreach board's chip.
 */
export function formatDayLabel(due) {
    const key = dueDayKey(due);
    if (!key) return '';
    const { year, month, day } = dayKeyParts(key);
    return `${MONTH_NAMES[month].slice(0, 3)} ${day}, ${year}`;
}

/**
 * The due chip's sentence for a `dueState`: 'Overdue · Oct 20', 'Due today', 'Due tomorrow', 'Due Oct 24'.
 * `state` is passed in because a finished task is 'closed' and never reads Overdue.
 */
export function dueText(due, state, zone, now = new Date()) {
    const label = formatDue(due, zone, now);
    if (state === 'overdue') return `Overdue · ${label}`;
    return label === 'Today' || label === 'Tomorrow' ? `Due ${label.toLowerCase()}` : `Due ${label}`;
}

const DUE_TEXT_TONES = {
    overdue: 'text-red-600 font-medium',
    today: 'text-amber-600 font-medium',
    upcoming: 'text-stone-500',
    none: 'text-stone-400',
    closed: 'text-stone-400',
};

/** Text classes for a `dueState` ('closed' mutes a finished task's date). */
export const dueTone = (state) => DUE_TEXT_TONES[state] || DUE_TEXT_TONES.none;

const createdAtMs = (task) => parseInstant(task?.created_at)?.getTime() || 0;

/**
 * Sort order for a task list: earliest due day first, tasks with no date LAST,
 * ties newest-created first. Client-side on purpose — Mongo sorts a missing or
 * null `due_date` before every date on an ascending sort, and its test double
 * sorts it last, so a server sort cannot be relied on to put them last.
 */
export function compareByDue(a, b) {
    const keyA = dueDayKey(a?.due_date);
    const keyB = dueDayKey(b?.due_date);
    if (keyA && keyB && keyA !== keyB) return keyA < keyB ? -1 : 1;
    if (keyA && !keyB) return -1;
    if (!keyA && keyB) return 1;
    return createdAtMs(b) - createdAtMs(a);
}
